import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider, Modal, Form, Input, Checkbox, message } from 'antd';
import { ArrowLeftOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './AboutPage.css';

const { Title, Text, Paragraph } = Typography;

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pageLoadTime] = useState(Date.now());
  
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('waitlist_session_id');
    if (existing) return existing;
    
    const generateId = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    };
    
    const newId = generateId();
    localStorage.setItem('waitlist_session_id', newId);
    return newId;
  });

  // Set page metadata
  useEffect(() => {
    document.title = 'About SNS Intelligence | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'About SNS Intelligence - Pharmaceutical market intelligence and structured analytical insights from SNS Research.');
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    
    message.success('You\'re on the list. We\'ll reach out when your access is approved.');
    setIsSubmitted(true);
    form.resetFields();
    setIsSubmitting(true);
    
    try {
      const hiddenFields = {
        referrer_url: document.referrer || undefined,
        landing_path: window.location.pathname,
        session_id: sessionId,
        honeypot: '',
        submit_timing: Date.now() - pageLoadTime
      };

      const response = await fetch('https://pharmadashv2-backend.onrender.com/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          marketing_opt_in: values.marketing_opt_in,
          ...hiddenFields
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const result = await response.json();
      
      if (result.status === 'accepted' || result.status === 'ok') {
        // Success - already shown message
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateCompanyEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter your email'));
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Please enter a valid email'));
    }
    
    // List of free email providers to reject
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com',
      'gmx.com', 'zoho.com', 'msn.com', 'inbox.com', 'mail.ru'
    ];
    
    const emailDomain = value.toLowerCase().split('@')[1];
    
    if (freeEmailProviders.includes(emailDomain)) {
      return Promise.reject(new Error('Please use your company email address'));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="about-page">
      {/* Navigation */}
      <nav className="about-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="about-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="about-header">
              <Title level={1} className="about-title">
                About SNS Intelligence
              </Title>
            </div>

            <Card className="about-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Title level={2} className="section-title">
                    Who We Are
                  </Title>
                  <Paragraph>
                    SNS Intelligence is an initiative by SNS Research, focused on pharmaceutical market intelligence and structured analytical insights for professional and corporate users. The platform represents a research effort dedicated to delivering curated data and analytical frameworks that support strategic and commercial decision-making in pharmaceutical markets.
                  </Paragraph>
                  <Paragraph>
                    The initiative emphasizes structured insights designed for professional use, serving organizations that require consistent, comparable market intelligence across therapeutic areas and geographic markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    What We Do
                  </Title>
                  <Paragraph>
                    SNS Intelligence plays a role in the pharmaceutical ecosystem by delivering market, pipeline, and competitive intelligence that supports strategic and commercial decision-making. The platform provides structured analytical frameworks that enable professionals to assess opportunity, evaluate competitive dynamics, and prioritize resource allocation across therapeutic markets.
                  </Paragraph>
                  <Paragraph>
                    The intelligence delivered emphasizes relevance, structure, and usability for decision-making. By maintaining consistent methodologies and analytical frameworks, the platform enables meaningful comparison across markets while supporting the granularity needed for within-market strategic assessment.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    How We Work
                  </Title>
                  <Paragraph>
                    SNS Intelligence is committed to structured methodologies and analytical consistency. The platform emphasizes careful curation of data, defined analytical frameworks, and comparability across markets. This approach ensures that market intelligence maintains consistency in structure and methodology, enabling professionals to make informed comparisons and strategic assessments.
                  </Paragraph>
                  <Paragraph>
                    The emphasis on structure and consistency supports decision-making by providing analytical frameworks that are comparable across therapeutic areas and geographic markets, while maintaining the granularity needed for strategic assessment within specific market contexts.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Who Uses SNS Intelligence
                  </Title>
                  <Paragraph>
                    SNS Intelligence serves professionals and organizations that require structured pharmaceutical market intelligence for strategic and commercial decision-making. Typical users include pharmaceutical companies, biotechnology firms, consulting and advisory teams, and corporate strategy and business development functions.
                  </Paragraph>
                  <Paragraph>
                    These users rely on the platform for market intelligence, pipeline analysis, competitive landscape assessment, and strategic planning activities that require consistent, comparable analytical frameworks across therapeutic markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Access and Use
                  </Title>
                  <Paragraph>
                    SNS Intelligence is a subscription-based platform with controlled access, intended for professional and corporate use. Access is managed through organizational subscriptions, ensuring appropriate use and data security. The platform emphasizes responsible data usage and access management, maintaining controlled access for corporate and professional users.
                  </Paragraph>
                  <Paragraph>
                    If you are interested in learning more about how the platform supports pharmaceutical market analysis and strategic decision-making, or to discuss your organization's specific analytical needs, we invite you to request access or contact our team to schedule a platform overview.
                  </Paragraph>
                  <div style={{ marginTop: '24px' }}>
                    <Space size="middle">
                      <Button 
                        type="primary" 
                        size="large"
                        onClick={showModal}
                      >
                        Request Free Trial
                      </Button>
                      <Button 
                        size="large"
                        onClick={() => {
                          navigate('/');
                          // Scroll to pricing section after navigation
                          setTimeout(() => {
                            const pricingSection = document.getElementById('pricing');
                            if (pricingSection) {
                              pricingSection.scrollIntoView({ behavior: 'smooth' });
                            } else {
                              // Fallback: try using hash
                              window.location.hash = 'pricing';
                            }
                          }, 300);
                        }}
                      >
                        View Pricing
                      </Button>
                    </Space>
                  </div>
                  <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e8e8e8' }}>
                    <Text style={{ color: '#595959', fontSize: '16px' }}>
                      Have questions? Contact us at{' '}
                      <a 
                        href="mailto:info@snsintel.com" 
                        style={{ 
                          color: '#1890ff', 
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        info@snsintel.com
                      </a>
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="about-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Trial Request Modal */}
      <Modal
        title="Request Free Trial"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        {!isSubmitted ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              label="Company Email"
              rules={[{ validator: validateCompanyEmail }]}
            >
              <Input 
                placeholder="your.email@company.com" 
                size="large"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="marketing_opt_in"
              valuePropName="checked"
              rules={[{ 
                validator: (_, value) => 
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept to continue'))
              }]}
            >
              <Checkbox>
                I agree to receive updates about the trial and marketing communications.
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                block
                htmlType="submit"
              >
                Request Free Trial
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={4}>You're on the list!</Title>
            <Paragraph>
              We'll reach out when your access is approved.
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AboutPage;
